import * as React from "react";
import { useState, useEffect } from "react";
import {
  Checkbox,
  Column,
  Columns,
  Divider,
  Heading,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Text,
  TextField,
  TextFieldBase,
} from "@origin-digital/ods-core";
import {
  Field,
  FieldInputProps,
  FieldMetaProps,
  FieldProps,
  FormikProps,
  useFormikContext,
} from "formik";
import FirstLastName from "./FormComponents/FirstLastName";
import PhoneNumber from "./FormComponents/PhoneNumber";
import EmailCommunications from "./FormComponents/EmailCommunications";
import BillingAddress from "./FormComponents/BillingAddress";
import { styled } from "@mui/material";
import { AccountPayer, FormValues } from "./types";
import { apiClient } from "src/api/apiService";
import FormContext from "src/context/FormContext";
import { EICStatusEnum } from "../Premises/types";
import {
  accountBlockCompanyEmptyFields,
  formikFieldsBatchUpdater,
  secondaryAccountEmptyFields,
  sendEicComms,
} from "./helper";
import FormFieldLayout from "./FormComponents/FormFieldLayout";
import ClaimFileUpload from "./Claim/ClaimFileUpload";
import OnlineFormAccountHolderDetails from "./OnlineFormAccountHolderDetails";
import { useNavigate } from "react-router-dom";
import { DateOfBirthFormikField } from "../Fields/DateOfBirth";

const StyledRadioGroup = styled(RadioGroup)({
  display: "flex",
  justifyContent: "flexStart",
});

interface IAccountBlock {
  referenceNumber?: string | null;
  accountPayer?: AccountPayer | null;
  msaNumber?: string | null;
  projectId?: string | null;
  siteSupervisorId?: string | null;
  dbApplicationId?: string | null;
  journeyId?: string | null;
}

const ClaimsAccountBlock = (props: IAccountBlock) => {
  const {
    referenceNumber,
    accountPayer,
    msaNumber,
    projectId,
    siteSupervisorId,
    dbApplicationId,
    journeyId,
  } = props;
  const { values, setFieldValue, errors, setErrors } =
    useFormikContext<FormValues>();
  const [savingBlock, setSavingBlock] = useState(false);
  const { setDisplayingSnackbar, setDisplayingAlertBanner } =
    React.useContext(FormContext);
  const navigate = useNavigate();
  const isDigitalEic = values.accountHolderDetailsType === "online";

  //cleaning up account block ui pathways
  useEffect(() => {
    if (values.accountHolder === "person") {
      formikFieldsBatchUpdater(setFieldValue, accountBlockCompanyEmptyFields);
    }
  }, [values.accountHolder]);
  useEffect(() => {
    if (values.secondaryAccountHolder === false) {
      formikFieldsBatchUpdater(setFieldValue, secondaryAccountEmptyFields);
    }
  }, [values.secondaryAccountHolder]);
  //end cleanup
  const hyperlinkText = (
    <>
      <Text>
        Request for a new energy account form is to be signed by the account
        holder. Please check that all the fields match what's on the form to
        avoid unnecessary delays.
      </Text>
    </>
  );

  const sendEicCommsOnSave = async (premiseId: string, requestId: string) => {
    if (
      isDigitalEic &&
      values.electricityEicStatus !== EICStatusEnum.EIC_ACCEPTED
    ) {
      const { valid, message } = await sendEicComms(
        premiseId,
        requestId,
        "Notification re-sent.",
        "Account failed to save."
      );
      if (valid) {
        setDisplayingAlertBanner(null);
        navigate("/", {
          state: {
            submitted: true,
            isDigitalEic,
            snackbarMessage: message,
          },
        });
      } else {
        setDisplayingSnackbar({
          message,
          tone: valid ? "positive" : "caution",
        });
      }
    }
  };

  const saveAccountBlock = async () => {
    setSavingBlock(true);
    try {
      const { data } = await apiClient.post(`/claim/draft`, {
        reference_number: referenceNumber,
        account_payer: accountPayer,
        msa_number: msaNumber,
        project_id: projectId,
        site_supervisor_id: siteSupervisorId,
        db_application_id: dbApplicationId,
        journey_id: journeyId,
        account_details: {
          first_name: values.accountPrimaryFirstName,
          last_name: values.accountPrimaryLastName,
          email: values.accountPrimaryEmail,
          is_digital_eic: isDigitalEic,
        },
      });
      setSavingBlock(false);
      setDisplayingSnackbar({
        message: "Account saved",
        tone: "positive",
      });
      await sendEicCommsOnSave(data.premise.id, data.services?.[0]?.request_id);
    } catch (e) {
      console.error("Failed to save account:", e);
      setDisplayingSnackbar({
        message: "Failed to save account",
        tone: "caution",
      });
      setSavingBlock(false);
    }
  };

  return (
    <>
      <Stack
        space={{
          xs: "xlarge",
          md: "xxlarge",
        }}
      >
        <Field name="accountHolderDetailsType" id="account-holder-details-type">
          {({ field }: { field: FormikProps<FieldProps> }) => (
            <StyledRadioGroup
              {...field}
              id="account-holder-details-type"
              name="accountHolderDetailsType"
              label="How would you like to submit account holder details?"
              value={values.accountHolderDetailsType}
            >
              <Radio id="online" label="Online form" value="online" />
              <Radio id="pdf" label="Upload PDF form" value="pdf" />
            </StyledRadioGroup>
          )}
        </Field>
        {values.accountHolderDetailsType === "online" && (
          <OnlineFormAccountHolderDetails
            values={values}
            setErrors={setErrors}
            savingBlock={savingBlock}
            setSavingBlock={setSavingBlock}
            saveAccountBlock={saveAccountBlock}
            homeUser={false}
            saveButtonText="Lodge claim"
          />
        )}
        {values.accountHolderDetailsType === "pdf" ? (
          <Stack
            space={{
              xs: "xlarge",
              md: "xxlarge",
            }}
          >
            <Field name="accountHolder" id="account-holder">
              {({ field }: { field: FormikProps<FieldProps> }) => (
                <StyledRadioGroup
                  {...field}
                  id="account-holder"
                  name="accountHolder"
                  label="Account holder"
                  value={values.accountHolder}
                >
                  <Radio id="person" label="Personal" value="person" />
                  <Radio id="company" label="Business" value="company" />
                </StyledRadioGroup>
              )}
            </Field>
            {values.accountHolder === "company" ? (
              <Field name="companyName" id="company-name">
                {({
                  field,
                  meta,
                }: {
                  field: FormikProps<FieldProps>;
                  meta: FieldMetaProps<FormValues>;
                }) => (
                  <TextField
                    {...field}
                    name="companyName"
                    id="company-name"
                    variant="outlined"
                    label="Company name"
                    hideHelperTextSpace={true}
                    error={!!meta.error}
                    helperText={errors.companyName}
                  />
                )}
              </Field>
            ) : null}
            {values.accountHolder === "company" ? (
              <Columns
                collapseBelow="md"
                space={{
                  xs: "none",
                  md: "medium",
                }}
              >
                <Column>
                  <Field name="abn" id="abn">
                    {({
                      field,
                      meta,
                    }: {
                      field: FormikProps<FieldProps>;
                      meta: FieldMetaProps<FormValues>;
                    }) => (
                      <TextField
                        {...field}
                        id="abn"
                        name="abn"
                        variant="outlined"
                        label="ABN"
                        hideHelperTextSpace={true}
                        error={!!meta.error}
                        helperText={meta.error}
                      />
                    )}
                  </Field>
                </Column>
                <Column />
              </Columns>
            ) : null}

            {values.accountHolder === "company" ? <Divider /> : null}
            {values.accountHolder === "company" ? (
              <Heading variant="h4">Primary contact</Heading>
            ) : null}
            {/* First + last name */}
            <FirstLastName homeUser={false} />
            <Columns collapseBelow="md">
              <Column width="2/3">
                <Columns collapseBelow="md">
                  <Column>
                    <DateOfBirthFormikField
                      id="account-primary-dob"
                      name="accountPrimaryDob"
                      label="Date of birth"
                    />
                  </Column>
                  <Column>
                    <Field
                      name="accountPrimaryPhone"
                      id="account-phone-number"
                      component={PhoneNumber}
                    />
                  </Column>
                </Columns>
              </Column>
              <Column width="1/3" />
            </Columns>
            <EmailCommunications
              id="account-primary-email"
              name="accountPrimaryEmail"
              homeUser={false}
            />
            <BillingAddress />
            {values.accountHolder === "person" ? <Divider /> : null}
            {values.accountHolder === "person" ? (
              <Columns
                collapseBelow="md"
                space={{
                  xs: "none",
                  md: "xlarge",
                }}
                alignY="stretch"
              >
                {/* Fields */}
                <Column width="2/3">
                  <Stack
                    space={{
                      xs: "xlarge",
                      md: "xxlarge",
                    }}
                  >
                    <Switch
                      id="secondary-account-holder"
                      label="Secondary account holder"
                      //@ts-expect-error: need item callback
                      onChange={(e, item) => {
                        setFieldValue("secondaryAccountHolder", item);
                      }}
                      checked={values.secondaryAccountHolder}
                    />
                    {values.secondaryAccountHolder ? (
                      <Stack>
                        <Columns
                          collapseBelow="md"
                          space={{
                            xs: "xlarge",
                            md: "medium",
                          }}
                        >
                          <Column>
                            <Field
                              name="accountSecondaryFirstName"
                              id="account-secondary-first-name"
                            >
                              {({
                                field,
                                meta,
                              }: {
                                field: FormikProps<FieldProps>;
                                meta: FieldMetaProps<FormValues>;
                              }) => (
                                <TextField
                                  {...field}
                                  id="account-secondary-first-name"
                                  variant="outlined"
                                  label="First name"
                                  hideHelperTextSpace={true}
                                  error={!!meta.error}
                                  helperText={meta.error}
                                />
                              )}
                            </Field>
                          </Column>
                          <Column>
                            <Field
                              name="accountSecondaryLastName"
                              id="account-secondary-last-name"
                            >
                              {({
                                field,
                                meta,
                              }: {
                                field: FormikProps<FieldProps>;
                                meta: FieldMetaProps<FormValues>;
                              }) => (
                                <TextField
                                  {...field}
                                  id="account-secondary-last-name"
                                  variant="outlined"
                                  label="Last name"
                                  hideHelperTextSpace={true}
                                  error={!!meta.error}
                                  helperText={meta.error}
                                />
                              )}
                            </Field>
                          </Column>
                        </Columns>
                        <Columns
                          collapseBelow="md"
                          space={{
                            xs: "xlarge",
                            md: "medium",
                          }}
                        >
                          <Column>
                            <DateOfBirthFormikField
                              id="account-secondary-dob"
                              name="accountSecondaryDob"
                              label="Date of birth"
                            />
                          </Column>
                          <Column>
                            <Field
                              name="accountSecondaryPhone"
                              id="account-secondary-phone"
                            >
                              {({
                                field,
                                meta,
                              }: {
                                field: FormikProps<FieldProps>;
                                meta: FieldMetaProps<FormValues>;
                              }) => (
                                <TextFieldBase
                                  {...field}
                                  id="account-phone"
                                  variant="outlined"
                                  label="Phone"
                                  error={!!meta.error}
                                  helperText={meta.error}
                                  // hideHelperTextSpace={true}
                                />
                              )}
                            </Field>
                          </Column>
                        </Columns>
                        <Field
                          name="accountSecondaryEmail"
                          id="account-secondary-email"
                        >
                          {({
                            field,
                            meta,
                          }: {
                            field: FormikProps<FieldProps>;
                            meta: FieldMetaProps<FormValues>;
                          }) => (
                            <TextField
                              {...field}
                              id="account-email"
                              variant="outlined"
                              label="Email"
                              error={!!meta.error}
                              helperText={meta.error}
                              hideHelperTextSpace={true}
                            />
                          )}
                        </Field>

                        <Field type="checkbox" name="addSecondaryNameToBill">
                          {({ field }: { field: FieldInputProps<boolean> }) => {
                            return (
                              <Checkbox
                                label="Add secondary account holder's name to Origin bills"
                                id="addSecondaryNameToBill"
                                checked={field.checked}
                                onChange={(e) =>
                                  setFieldValue(
                                    "addSecondaryNameToBill",
                                    e.target.checked
                                  )
                                }
                              />
                            );
                          }}
                        </Field>
                      </Stack>
                    ) : null}
                    {/* End Email */}
                  </Stack>
                </Column>
                {/* End Fields */}
                {/* HelperText */}
                <Column width="1/3">
                  <Stack>
                    {
                      <Text variant="body-small" tone="neutralLight">
                        The secondary account holder will be able to access all
                        of the primary account holder's Origin accounts.
                      </Text>
                    }
                  </Stack>
                </Column>
                {/* End HelperText */}
              </Columns>
            ) : null}
            <Divider />
            <FormFieldLayout text={hyperlinkText} multipleFields>
              <Text>Customer consent</Text>
              <Field name="claimsCustomerConsentFiles">
                {({ field }: FieldProps) => <ClaimFileUpload field={field} />}
              </Field>
            </FormFieldLayout>
          </Stack>
        ) : null}
        {/* </Accordion> */}
      </Stack>
      {/* End Premise section */}
    </>
  );
};

export default ClaimsAccountBlock;
